<template>
  <div
    class="text-widget pb-mobile-responsive"
    :class="widgetClasses"
    :style="widgetStyles"
    v-html="processedContent"
  ></div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { TextWidget } from '@/types/pagebuilder'
import { processTextContent } from '@/utils/textProcessing'
import {
  resolveTextWidgetFontSize,
  stripInlineTypography,
} from '@/utils/textWidgetUtils'
import { useWidgetStyles } from '@/composables/useWidgetStyles'
import { assignResponsiveCssVar } from '@/utils/responsiveWidgetSize'

interface Props {
  widget: TextWidget
}

const props = defineProps<Props>()
const { widgetStyles: layoutStyles, widgetClasses: layoutClasses } = useWidgetStyles(props.widget)

const textColor = computed(() => props.widget.textColor || '#333333')
const resolvedFontSize = computed(() => resolveTextWidgetFontSize(props.widget.fontSize))

const widgetStyles = computed(() => {
  const styles: Record<string, string> = {
    ...layoutStyles.value,
    textAlign: props.widget.textAlign || 'left',
    color: textColor.value,
    '--widget-text-color': textColor.value,
  }

  delete styles.fontSize
  delete styles.padding

  assignResponsiveCssVar(
    styles,
    '--pb-font-size',
    resolvedFontSize.value,
    props.widget.fontSizeMobile,
  )
  assignResponsiveCssVar(
    styles,
    '--pb-padding',
    props.widget.padding || '0',
    props.widget.paddingMobile,
  )

  if (props.widget.fontFamily) {
    styles.fontFamily = props.widget.fontFamily
    styles['--widget-font-family'] = props.widget.fontFamily
  }
  if (props.widget.fontWeight) {
    styles.fontWeight = props.widget.fontWeight
    styles['--widget-font-weight'] = props.widget.fontWeight
  }
  if (props.widget.fontStyle) {
    styles.fontStyle = props.widget.fontStyle
  }
  if (props.widget.lineHeight) {
    styles.lineHeight = props.widget.lineHeight
  }
  if (props.widget.letterSpacing) {
    styles.letterSpacing = props.widget.letterSpacing
  }

  if (!props.widget.padding && !props.widget.paddingMobile) styles.padding = '0'
  if (!props.widget.margin && !props.widget.width && !props.widget.maxWidth) {
    styles.margin = '0'
  }

  return styles
})

const widgetClasses = computed(() => {
  const classes: string[] = []
  if (layoutClasses.value) classes.push(layoutClasses.value)
  if (props.widget.customClass) classes.push(props.widget.customClass)
  if (props.widget.entranceAnimation && props.widget.entranceAnimation !== 'none') {
    classes.push('widget-animated')
  }
  return classes.join(' ')
})

const processedContent = computed(() => {
  let content = processTextContent(props.widget.content || '')
  content = stripInlineTypography(content, {
    color: Boolean(props.widget.textColor),
    fontFamily: true,
    fontSize: true,
  })
  return content
})
</script>

<style scoped>
.text-widget {
  line-height: 1.6;
  max-width: 100%;
  box-sizing: border-box;
  font-family: var(--widget-font-family, inherit);
  font-size: var(--pb-font-size, var(--widget-font-size, 1rem));
  font-weight: var(--widget-font-weight, inherit);
}

.text-widget :deep(p),
.text-widget :deep(h1),
.text-widget :deep(h2),
.text-widget :deep(h3),
.text-widget :deep(h4),
.text-widget :deep(h5),
.text-widget :deep(h6),
.text-widget :deep(li),
.text-widget :deep(span),
.text-widget :deep(div),
.text-widget :deep(blockquote),
.text-widget :deep(strong),
.text-widget :deep(em) {
  color: var(--widget-text-color, inherit) !important;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  letter-spacing: inherit;
}

.text-widget :deep(h1) {
  font-size: 1.5em;
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.text-widget :deep(h2) {
  font-size: 1.3em;
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.text-widget :deep(h3) {
  font-size: 1.15em;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.text-widget :deep(h4),
.text-widget :deep(h5),
.text-widget :deep(h6) {
  font-size: 1.05em;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.text-widget :deep(strong),
.text-widget :deep(b) {
  font-weight: 700;
}

.text-widget :deep(p) {
  margin-bottom: 1rem;
}

.text-widget :deep(ul),
.text-widget :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.text-widget :deep(a) {
  color: var(--widget-text-color, #007bff);
  text-decoration: underline;
  opacity: 0.92;
}

.text-widget :deep(a:hover) {
  opacity: 1;
}
</style>
